import { Trans } from "react-i18next";
import { z } from "zod";

import { PageContent } from "@/components/page";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/lib/utils";
import { useShop } from "@/hooks/use-shop";
import { useUser } from "@/hooks/use-user";

export const priceSchema = z.object({
  newBalance: z.coerce.number(),
  oldBalance: z.coerce.number(),
  price: z.coerce.number(),
});

export type Price = z.infer<typeof priceSchema>;

/** Old balance, price and new balance, and the button that approves the charge. */
export function BalancePaymentConfirm({
  price,
  pending,
  showTitle = true,
  onConfirm,
}: {
  price: Price;
  pending: boolean;
  // the payment flow's header already says "Kérjük fizess!"
  showTitle?: boolean;
  onConfirm: () => void;
}) {
  const { data: shop } = useShop();
  const { data: user } = useUser();
  const currencyCode = shop.currencyCode ?? "HUF";

  return (
    <PageContent>
      {showTitle && (
        <h2 className="font-helvetica font-bold text-6xl text-center mb-16">
          <Trans>Kérjük fizess!</Trans>
        </h2>
      )}
      <div className="grid grid-cols-3 justify-center gap-x-5 mb-12">
        <div className="text-center text-3xl">
          <p className="mb-6">
            <Trans>Korábbi egyenleged</Trans>
          </p>
          <p className="font-helvetica font-bold">
            {formatCurrency(price.oldBalance, currencyCode)}
          </p>
        </div>
        <div className="text-center text-5xl">
          <p className="mb-6">
            <Trans>Fizetendő</Trans>
          </p>
          <p className="font-helvetica font-bold">
            {formatCurrency(price.price, currencyCode)}
          </p>
        </div>
        <div className="text-center text-3xl">
          <p className="mb-6">
            <Trans>Új egyenleged</Trans>
          </p>
          <p className="font-helvetica font-bold">
            {formatCurrency(price.newBalance, currencyCode)}
          </p>
        </div>
      </div>
      <div className="flex justify-center mb-16">
        <Button className="mx-auto" onClick={onConfirm} disabled={pending}>
          <Trans>Engedélyezem</Trans>
        </Button>
      </div>
      <p className="text-[#f45e22] flex gap-x-3 items-center text-2xl mb-4">
        <img src="/exclamation.svg" className="size-6" />
        <Trans>
          Bubbles egyenlegedről leemeljük az összeget. Kérjük, hagyd jóvá a
          tranzakciót!
        </Trans>
      </p>
      {shop.canGenerateInvoices && user && (
        <p className="flex gap-x-1 items-center text-2xl">
          <img src="/arrow-attention.svg" className="size-8" />
          <Trans>
            Számládat egyszerűen letöltheted a my.bubbles.hu oldalról.
          </Trans>
        </p>
      )}
    </PageContent>
  );
}

export function BalancePaymentHelpText() {
  return (
    <Trans>
      Bubbles egyenlegről való fizetés esetén a mosás/szárítás ára a Bubbles
      egyenlegedről kerül levonásra.
    </Trans>
  );
}
